<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content>
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Data Repair Tools</h1>
      </base-content>
    </div>

    <base-content class="tw-mt-6">
      <base-card>
        <div class="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-start sm:tw-items-center">
          <div class="tw-flex-1">
            <h2 class="tw-text-lg tw-font-semibold tw-text-gray-800">Resolve Patient Tracking Issues</h2>
            <p class="tw-text-sm tw-text-gray-500 tw-mt-1 tw-max-w-2xl">
              This tool repairs historical data where missing pharmacy order IDs prevented status logs from being created. It will scan for broken records, extract IDs from API
              history, and reconstruct missing tracking logs.
            </p>
          </div>
          <div class="tw-mt-4 sm:tw-mt-0 sm:tw-ml-6">
            <base-button @click="runRepair" :loading="isLoading" :disabled="isLoading" type="primary" color="primary">
              Run Repair Process
            </base-button>
          </div>
        </div>

        <!-- Progress / Results -->
        <div v-if="result" class="tw-mt-8 tw-border-t tw-pt-6">
          <h3 class="tw-text-md tw-font-medium tw-text-gray-900 tw-mb-4">Results</h3>

          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
            <div class="tw-bg-blue-50 tw-p-4 tw-rounded-lg tw-border tw-border-blue-100">
              <div class="tw-text-blue-600 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wider">Records Evaluated</div>
              <div class="tw-text-3xl tw-font-bold tw-text-blue-900 tw-mt-1">{{ result.recordsProcessed }}</div>
            </div>
            <div class="tw-bg-green-50 tw-p-4 tw-rounded-lg tw-border tw-border-green-100">
              <div class="tw-text-green-600 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wider">Records Successfully Repaired</div>
              <div class="tw-text-3xl tw-font-bold tw-text-green-900 tw-mt-1">{{ result.recordsUpdated }}</div>
            </div>
          </div>

          <!-- Error Details -->
          <div v-if="result.errorDetails && result.errorDetails.length > 0" class="tw-mt-6">
            <div class="tw-text-red-600 tw-text-sm tw-font-semibold tw-mb-2">Errors Encountered</div>
            <div class="tw-bg-red-50 tw-p-4 tw-rounded-lg tw-border tw-border-red-100 tw-max-h-60 tw-overflow-y-auto">
              <ul class="tw-list-disc tw-list-inside tw-text-sm tw-text-red-800">
                <li v-for="(err, idx) in result.errorDetails" :key="idx" class="tw-mb-1">{{ err }}</li>
              </ul>
            </div>
          </div>

          <!-- Raw View -->
          <div class="tw-mt-6">
            <base-button type="text" color="gray" size="sm" @click="showRaw = !showRaw"> {{ showRaw ? "Hide" : "Show" }} Raw Response </base-button>
            <pre v-if="showRaw" class="tw-mt-2 tw-bg-gray-100 tw-p-4 tw-rounded tw-text-xs tw-overflow-x-auto">{{ JSON.stringify(result, null, 2) }}</pre>
          </div>
        </div>
      </base-card>
    </base-content>
  </div>
</template>

<script>
export default {
  props: {
    businessId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      result: null,
      showRaw: false,
    };
  },
  methods: {
    async runRepair() {
      this.isLoading = true;
      this.result = null;
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/resolve-patient-tracking-issues`);
        this.result = response.data;

        if (this.result.success) {
          this.EventBus.$emit("GLOBAL_SUCCESS", {
            title: "Repair Process Completed",
            message: `Successfully processed ${this.result.recordsProcessed} records and updated ${this.result.recordsUpdated}.`,
          });
        } else {
          throw new Error(this.result.message || "Repair process failed");
        }
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Repair Failed",
          message: (error.response && error.response.data && error.response.data.message) || error.message || "An unexpected error occurred during the repair process.",
          error: error,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
